<script>
  import { format_date, localize_utc_date } from '#lib/util.js';
  import { get_week_number_for_start_of_month } from '#lib/patch_util.js';
  import MonthSheet from '#lib/MonthSheet.svelte';

  import VirtualScroll from '#lib/VirtualScroll.svelte';

  // Localize the current date to the business timezone.
  const current_local_date = localize_utc_date(new Date());
  const current_local_year = current_local_date.getUTCFullYear();
  const current_local_month = current_local_date.getUTCMonth();

  let { 
    /** @type {number} The minimum year to show */
    min_year = current_local_year,
    /** @type {number} The maximum year to show */
    max_year = current_local_year + 4,
    /** @type {() => void} Callback when a month is selected */
    on_select,
  } = $props();

  let years = $derived(
    Array.from(
      { length: max_year - min_year + 1 }, 
      (_, i) => min_year + i
    )
  );
  
  function get_months_of_year(year) {
    return Array.from({ length: 12 }).map((_, i) => {
      // Create a UTC date for the first day of the month.
      const date_obj = new Date(Date.UTC(year, i, 1));
      // ISO week + week-based year of the month start (for January the ISO
      // year can be the previous calendar year, e.g. 2027-01-01 → week 53/2026)
      const { week, year: week_year } = get_week_number_for_start_of_month(year, i);
      return {
        value: i,
        title: format_date(date_obj, 'MMM'),
        week,
        week_year
      };
    });
  }


</script>


<div class='month-selector'>
  <VirtualScroll
    items={years}
    item_height={755}
    height='100%'
  >
    {#snippet children(year_in_focus)}
      <p class='year-label text-heading3 leading-trim px py p-2 my m-4'>{year_in_focus}</p>
      <div class="grid-of-months px py">
        {#each get_months_of_year(year_in_focus) as month_option}
          {@const is_displayed_month_current = 
             month_option.value === current_local_month && 
             year_in_focus === current_local_year}
          <button 
            class="month-button py p-4" 
            onclick={() => {
              on_select({
                week: month_option.week,
                week_year: month_option.week_year,
                year: year_in_focus,
                month: month_option.value
              });
            }}
          >
            <div class='month-display px py p-2 flex-column gap-y g-2 {is_displayed_month_current ? 'displayed-month-is-current' : ''}'>
              <p class='text-body month-name {is_displayed_month_current ? 'underline' : ''}'>{month_option.title}</p>
              <!-- TODO: move selection to inidivudal weeks, not entire month, for finer granular selection and matching user intend -->
              <MonthSheet 
                month={month_option.value} 
                year={year_in_focus} 
                show_adjacent_months={false} 
                show_weekdays={false}
              />
            </div>
          </button>
        {/each}
      </div>
    {/snippet}
  </VirtualScroll>
</div>

<style lang="postcss">
  .year-label, .grid-of-months {
    margin-inline: auto;
    padding-inline: var(--page-inline-spacing);
    max-width: calc(8 * 56px);
  }
  .month-selector {
    height: calc(100vh - var(--dynamic-island-bottom-edge) - var(--nav-height));
    width: 100%;
    @container (max-width: 375px) {
      :global(.viewport > .contents > .row) {
        height: fit-content !important;
      }
    }
    :global(.viewport) {
      scrollbar-width: thin;
    }
  }
  .grid-of-months {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    container-type: inline-size;
    width: min(100vw, 100%);
    .month-display {
      transition: background-color 0.1s ease-in-out;
      @media (hover: hover) {
        &:hover {
          background-color: var(--color-default-background-50);
        }
      }
    }
    .month-button:nth-child(3n+1) .month-display {
      padding-inline-start: 0;
    }
    .month-button:nth-child(3n+3) .month-display {
      padding-inline-end: 0;
    }
    /* hide full month sheet on smaller screens */
    @container (max-width: 320px) {      
      :global(.month-sheet) {
        display: none;
      }
    }
  }

  .month-sheet {
    transition: background-color 0.1s ease-in-out;
    @media (hover: hover) {
      &:hover {
        background-color: var(--color-default-background-50);
      }
    }
    &.displayed-month-is-current .month-name {
      --link-underline-color: var(--color-default-emphasis-background);
      --link-underline-thickness: 2px;
      --link-underline-offset: 0.3em;
      width: fit-content;
    }
  }
</style>
